import {
  marketPoolQuoteContextAtTime,
  quoteOutcomeSellExactIn,
  quoteOutcomeSellFeeExcludedExactIn,
} from '@morfi/backend-sdk-v2/client';
import { type OutcomePoolQuoteContext, resolveOutcomePoolQuoteVenue } from '@morfi/market-math';
import BN from 'bn.js';
import { computeTradeEstimate } from '@/hooks/trading/use-trade-estimate';

const BC_SLIPPAGE_BPS = 100;
const CP_SWAP_SLIPPAGE_BPS = 30;

export interface ChipBuyPlan {
  amountInD6: BN;
  walletDebitD6: BN;
  feeExcludedQuoteInputD6: BN;
  outcomeTokensOutD6: BN;
  desiredAmountD6: BN;
  slippageBps: number;
  suggestedSlippageBps: number;
  marketPhase: 'bonding_curve' | 'amm';
}

export interface ChipSellPlan {
  tokensNeededD6: BN;
  expectedNetUsdcOutD6: BN;
  /** Exact gross quote-output intent used by toast/share surfaces. */
  grossQuoteAmountD6: BN;
  /** Fee-excluded quote proceeds used by gross P&L accounting. */
  feeExcludedQuoteOutputD6: BN;
  /** Conviction penalty this exit pays (D6); zero off the bonding curve. */
  penaltyFeeD6: BN;
  slippageBps: number;
  suggestedSlippageBps: number;
  marketPhase: 'bonding_curve' | 'amm';
}

export function computeChipBuyPlan(args: {
  buyUsdc: number;
  context: OutcomePoolQuoteContext;
}): ChipBuyPlan | null {
  if (!(args.buyUsdc > 0)) return null;
  const estimate = computeTradeEstimate({
    direction: 'buy',
    amountUsdc: args.buyUsdc,
    context: args.context,
    maximumOutcomeAmountInD6: BigInt(0),
    slippageBps: BC_SLIPPAGE_BPS,
  });
  if (estimate === null || estimate.direction !== 'buy') return null;
  return {
    amountInD6: estimate.amountInD6,
    walletDebitD6: estimate.walletDebitD6,
    feeExcludedQuoteInputD6: estimate.feeExcludedQuoteInputD6,
    outcomeTokensOutD6: estimate.outcomeTokensOutD6,
    desiredAmountD6: estimate.amountInD6,
    slippageBps: estimate.suggestedSlippageBps,
    suggestedSlippageBps: estimate.suggestedSlippageBps,
    marketPhase: estimate.marketPhase,
  };
}

/** Plan an exact percentage sell without a display-currency reverse round-trip. */
export function computeChipSellPlan(args: {
  pct: number;
  indexedBalanceD6: bigint;
  claimRightsBalanceD6: bigint | null;
  onchainBalanceD6: bigint | null;
  onchainClaimRightsBalanceD6: bigint | null;
  context: OutcomePoolQuoteContext;
}): ChipSellPlan | null {
  if (!Number.isSafeInteger(args.pct) || args.pct <= 0 || args.pct > 100) return null;
  if (args.onchainBalanceD6 === null || args.onchainBalanceD6 < BigInt(0)) return null;

  let sellableD6 = minimum(args.indexedBalanceD6, args.onchainBalanceD6);
  if (args.context.poolModel === 'bonding_curve') {
    if (
      args.claimRightsBalanceD6 === null ||
      args.onchainClaimRightsBalanceD6 === null ||
      args.onchainClaimRightsBalanceD6 < BigInt(0)
    ) {
      return null;
    }
    sellableD6 = minimum(
      sellableD6,
      minimum(args.claimRightsBalanceD6, args.onchainClaimRightsBalanceD6),
    );
  }
  const tokensToSellD6 = (sellableD6 * BigInt(args.pct)) / BigInt(100);
  if (tokensToSellD6 <= BigInt(0)) return null;

  const context = marketPoolQuoteContextAtTime(args.context);
  const quote = quoteOutcomeSellExactIn({
    context,
    outcomeAmountInD6: tokensToSellD6,
  });
  if (!quote.available) return null;
  const feeExcludedQuote = quoteOutcomeSellFeeExcludedExactIn({
    context,
    outcomeAmountInD6: tokensToSellD6,
  });
  if (!feeExcludedQuote.available) return null;
  const venue = resolveOutcomePoolQuoteVenue(args.context);
  if (!venue.available) return null;

  const grossD6 =
    quote.value.venue === 'bonding_curve'
      ? quote.value.grossQuoteOutputD6
      : quote.value.curveOutputAmountD6;
  const netD6 =
    quote.value.venue === 'bonding_curve'
      ? quote.value.netQuoteOutputD6
      : quote.value.outputAmountD6;
  const suggestedSlippageBps =
    venue.value === 'bonding_curve' ? BC_SLIPPAGE_BPS : CP_SWAP_SLIPPAGE_BPS;

  return {
    tokensNeededD6: new BN(tokensToSellD6.toString()),
    expectedNetUsdcOutD6: new BN(netD6.toString()),
    grossQuoteAmountD6: new BN(grossD6.toString()),
    feeExcludedQuoteOutputD6: new BN(feeExcludedQuote.value.toString()),
    penaltyFeeD6: new BN(
      (quote.value.venue === 'bonding_curve' ? quote.value.sellPenaltyFeeD6 : BigInt(0)).toString(),
    ),
    slippageBps: suggestedSlippageBps,
    suggestedSlippageBps,
    marketPhase: venue.value === 'bonding_curve' ? 'bonding_curve' : 'amm',
  };
}

function minimum(left: bigint, right: bigint): bigint {
  return left < right ? left : right;
}
